import React from 'react';
import PropTypes from 'prop-types';
import {
    space, colors, fontSizes, fontWeights, radii
} from 'style/config';
import { wrapFunctionalComponent } from 'utils/framework';
import {
    Text, Divider, Image, Button, Flex, Link
} from 'components/ui';
import SubscriptionInfoSection from 'components/GlobalModals/ManageSubscriptionModal/components/SubscriptionInfoSection';

/**
 * Renders the base (non-confirm) sections of the Manage Subscription modal: product info,
 * subscription details, and the management actions gated by feature flags.
 */
function ManageSubscriptionBaseSections({
    creditCard,
    featureFlags,
    localization,
    onChangeDate,
    onChangeFrequency,
    onChangePayment,
    onChangeShipping,
    onPauseSubscription,
    onResume,
    onSkipDelivery,
    onUnsubscribeClick,
    product,
    savingsCopy,
    shippingInfo
}) {
    return (
        <Flex css={styles.contentContainer}>
            <Flex css={styles.operations}>
                <Text
                    is='h4'
                    css={styles.nextShipment}
                >
                    {localization.nextShipment} <Text css={styles.nextShipmentDate}>{shippingInfo.nextShipmentDate}</Text>
                </Text>

                {!!(featureFlags.isChangeDateEnabled && onChangeDate) && (
                    <Button
                        variant='primary'
                        children={localization.changeDate}
                        onClick={onChangeDate}
                    />
                )}
                {!!(featureFlags.isSkipPauseEnabled && onSkipDelivery && onPauseSubscription) && (
                    <Flex gap={space[3]}>
                        <Button
                            size='sm'
                            variant='secondary'
                            css={styles.operationButton}
                            onClick={onSkipDelivery}
                            children={localization.skip}
                        />
                        <Button
                            size='sm'
                            variant='secondary'
                            css={styles.operationButton}
                            onClick={onPauseSubscription}
                            children={localization.pause}
                        />
                    </Flex>
                )}
            </Flex>

            <Divider />

            <SubscriptionInfoSection
                isInteractive={featureFlags.isChangeFrequencyEnabled}
                onClick={onChangeFrequency}
                title={localization.deliveryTitle}
            >
                <Text>{localization.deliveryValue}</Text>
            </SubscriptionInfoSection>

            <Divider />

            <SubscriptionInfoSection
                isInteractive={featureFlags.isChangeAddressEnabled}
                onClick={onChangeShipping}
                title={localization.shippingTitle}
            >
                {shippingInfo.hasData ? (
                    <>
                        <Text
                            is='p'
                            fontWeight={fontWeights.bold}
                        >
                            {shippingInfo.fullName}
                        </Text>
                        <Text is='p'>
                            {shippingInfo.addressLine1}
                            <br />
                            {shippingInfo.fullAddress}
                            <br />
                            {shippingInfo.phoneNumber}
                        </Text>
                    </>
                ) : (
                    featureFlags.isChangeAddressEnabled && <Text>{localization.selectAddress}</Text>
                )}
            </SubscriptionInfoSection>

            <Divider />

            <SubscriptionInfoSection
                isInteractive={featureFlags.isChangePaymentEnabled}
                onClick={onChangePayment}
                title={localization.paymentTitle}
            >
                {creditCard.hasData ? (
                    <Flex gap={space[3]}>
                        <Image
                            height={32}
                            width={50}
                            src={creditCard.logo}
                            alt={creditCard.alt}
                        />
                        <Text is='p'>
                            {creditCard.label}
                            <br />
                            <span css={{ fontSize: fontSizes.sm }}>
                                {localization.creditCardExpDate} {creditCard.expDate}
                            </span>
                        </Text>
                    </Flex>
                ) : (
                    featureFlags.isChangePaymentEnabled && <Text>{localization.selectPayment}</Text>
                )}
            </SubscriptionInfoSection>

            <Text
                is='p'
                css={[styles.legalCopy, { fontSize: fontSizes.sm }]}
            >
                {localization.legalCopy}
            </Text>

            {onResume ? (
                <Text
                    is='p'
                    css={styles.annuallySaving}
                    dangerouslySetInnerHTML={{
                        __html: product.acceleratedPromotion ? savingsCopy.hotDealSaving : savingsCopy.annuallySaving
                    }}
                />
            ) : (
                <>
                    <Divider />

                    <Link
                        css={[styles.cancelSubscription, { color: colors.blue }]}
                        onClick={onUnsubscribeClick}
                    >
                        {localization.cancelSubscription}
                    </Link>
                </>
            )}
        </Flex>
    );
}

const styles = {
    nextShipment: {
        fontWeight: fontWeights.bold
    },
    nextShipmentDate: {
        color: colors.green
    },
    operations: {
        flexDirection: 'column',
        gap: space[2]
    },
    operationButton: {
        flexGrow: 1
    },
    contentContainer: {
        flexDirection: 'column',
        gap: space[4]
    },
    annuallySaving: {
        background: colors.lightGray,
        padding: `${space[2]}px ${space[3]}px`,
        borderRadius: radii[2]
    },
    legalCopy: {
        marginTop: -space[2]
    },
    cancelSubscription: {
        marginRight: 'auto'
    }
};

ManageSubscriptionBaseSections.propTypes = {
    creditCard: PropTypes.object.isRequired,
    featureFlags: PropTypes.object.isRequired,
    localization: PropTypes.object.isRequired,
    onChangeDate: PropTypes.func,
    onChangeFrequency: PropTypes.func,
    onChangePayment: PropTypes.func,
    onChangeShipping: PropTypes.func,
    onPauseSubscription: PropTypes.func,
    onResume: PropTypes.func,
    onSkipDelivery: PropTypes.func,
    onUnsubscribeClick: PropTypes.func,
    product: PropTypes.object.isRequired,
    savingsCopy: PropTypes.object.isRequired,
    shippingInfo: PropTypes.object.isRequired
};

export default wrapFunctionalComponent(ManageSubscriptionBaseSections, 'ManageSubscriptionBaseSections');
